import { Helmet } from "react-helmet-async";

const SITE_NAME = "Serverlistr";
const SITE_URL = process.env.REACT_APP_SITE_URL || "https://serverlistr.net";
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.png`;

/**
 * Universal SEO component — sets title, meta, Open Graph, Twitter Card,
 * canonical URL, and optional Schema.org JSON-LD.
 *
 * Props:
 * - title: page title (will be suffixed with site name unless titleTemplate is null)
 * - description: meta description
 * - path: canonical path (starts with /). Defaults to current pathname.
 * - image: absolute or path to OG image
 * - type: og:type — "website" (default) or "article"
 * - keywords: comma-separated string
 * - noindex: boolean — set robots meta to noindex
 * - schema: object or array of Schema.org JSON-LD objects
 */
export default function SEO({
  title,
  description,
  path,
  image,
  type = "website",
  keywords,
  noindex = false,
  schema,
}) {
  const canonicalPath =
    path ||
    (typeof window !== "undefined" ? window.location.pathname + window.location.search : "/");
  const canonical = `${SITE_URL}${canonicalPath}`.replace(/\/$/, canonicalPath === "/" ? "/" : "");
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} - Discord Server List & Directory`;
  const desc =
    description ||
    "Discover the best Discord servers on Serverlistr — the fastest-growing Discord server list and directory. Browse thousands of gaming, anime, community, and tech Discord servers.";
  const ogImage = image
    ? image.startsWith("http")
      ? image
      : `${SITE_URL}${image}`
    : DEFAULT_OG_IMAGE;

  const schemas = Array.isArray(schema) ? schema : schema ? [schema] : [];

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {keywords ? <meta name="keywords" content={keywords} /> : null}
      <link rel="canonical" href={canonical} />
      <meta name="robots" content={noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large"} />

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:alt" content={title || SITE_NAME} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImage} />

      {schemas.map((s, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(s)}</script>
      ))}
    </Helmet>
  );
}

export { SITE_NAME, SITE_URL };
